import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { ApiCoreCompany, ContractsTaxonomiesDocument } from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

import { getMatchedLegalEntities } from '../helper'
import { SalaryTypesByLegalEntity } from './SalaryTypesByLegalEntity'

const SalaryTypesStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const {
    access: { id: accessId },
    company: company,
  } = useCurrentSession()

  useOnNext(() => {
    const compensationTypeKeys = Object.keys(data.answers).filter((key) =>
      /^compensation_type_\d+$/.test(key)
    )
    const mappedCompensationTypeCount = compensationTypeKeys.map(
      (key) => !!data.answers[key]
    ).length

    track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsSalaryTypessMapped),
      {
        company_id: company.id,
        access_id: accessId,
        mapped_employee_groups: mappedCompensationTypeCount,
      }
  })

  const onCompensationTypeChanged = (compensationId: string, value: string) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        [`compensation_type_${compensationId}`]: value,
      },
    })
  }

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const matchedLegalEntities = getMatchedLegalEntities(data, company as ApiCoreCompany)

  const legalEntityIds = matchedLegalEntities?.map((legalEntity) => legalEntity.id)

  const { data: taxonomiesData, loading: taxonomiesLoading } = useQuery(
    ContractsTaxonomiesDocument,
    {
      variables: {
        legalEntityIds: legalEntityIds,
      },
    }
  )

  const taxonomies = taxonomiesData?.contracts.taxonomies || []
  const taxonomiesByLegalEntity = groupBy(taxonomies, (taxonomy) => taxonomy.legalEntityId)
  if (taxonomiesLoading) {
    return <Spinner />
  }
  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.wage_type.title'
          )}
          description={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.wage_type.description'
          )}
        />
        <Box justifyContent="flexStart" flexDirection="column" gap="s32">
          {matchedLegalEntities.map((legalEntity) => {
            const legalEntityTaxonomies = taxonomiesByLegalEntity[legalEntity.id] || []
            if (!legalEntityTaxonomies || legalEntityTaxonomies.length === 0) return null

            return (
              <SalaryTypesByLegalEntity
                data={data}
                key={`legal_entity_${legalEntity.id}`}
                legalEntity={legalEntity}
                compensationTypes={[]}
                onSelect={(compensationTypeId, value, _label) =>
                  onCompensationTypeChanged(compensationTypeId, value)
                }
              />
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export const salaryTypesStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.wage_type.wizard_title'
  ),
  nextText: i18n.t('common.save'),
  render: (props) => <SalaryTypesStep {...props} />,
  isCompleted: (_data) => true,
})
